import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { ApiError, apiUrl, clearToken, getDeviceId, setToken } from "../lib/api";

type LoginResponse = {
  data: { session_token: string; session: { id: string } };
};

/**
 * The login form posts the raw ADMIN_TOKEN to /v1/admin/login (one-off) and
 * receives a session token back. The session token is what every subsequent
 * request carries — the raw secret never lives in localStorage.
 *
 * On 403 BLOCKED the page surfaces "this IP is banned, retry in N min" and
 * keeps the form disabled. Six failed logins from a single IP within 15
 * minutes triggers a 1-hour ban (server-enforced; see admin_ip_bans).
 */
export function LoginPage({ onAuthed }: { onAuthed: (token: string) => void }) {
  const [token, setLocal] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    setError(null);

    try {
      const deviceId = getDeviceId();
      const res = await fetch(`${apiUrl}/v1/admin/login`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          admin_token: token.trim(),
          device_id: deviceId,
          device_label: deriveDeviceLabel(),
        }),
      });
      const json = (await res.json().catch(() => ({}))) as
        | LoginResponse
        | { error: { code: string; message: string } };

      if (!res.ok || "error" in json) {
        const apiErr =
          "error" in json
            ? new ApiError(json.error.code, json.error.message, res.status)
            : new ApiError("HTTP_ERROR", `HTTP ${res.status}`, res.status);
        clearToken();
        if (apiErr.code === "BLOCKED") {
          setError(`${apiErr.message}. Try again later.`);
        } else {
          setError(apiErr.message);
        }
        return;
      }

      setToken(json.data.session_token);
      onAuthed(json.data.session_token);
    } catch (e) {
      clearToken();
      const msg = e instanceof Error ? e.message : "Authentication failed";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-6 dark:bg-gray-950">
      <Card className="w-full max-w-sm">
        <form onSubmit={submit} className="space-y-4">
          <Input
            id="token"
            type="password"
            autoFocus
            autoComplete="current-password"
            spellCheck={false}
            value={token}
            onChange={(e) => setLocal(e.target.value)}
            hasError={error != null}
            placeholder="Password"
          />
          {error && (
            <p className="text-xs text-red-600 dark:text-red-500">{error}</p>
          )}
          <Button
            type="submit"
            className="w-full"
            isLoading={busy}
            loadingText="Verifying"
            disabled={!token.trim()}
          >
            Sign in
          </Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * Best-effort human label for the session row. Cheap heuristic: combine the
 * platform and a guess at the browser. We're not trying to fingerprint —
 * this is purely for the operator to recognise sessions in the UI.
 */
function deriveDeviceLabel(): string {
  const ua = navigator.userAgent ?? "";
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform ?? guessPlatform(ua);
  const browser = guessBrowser(ua);
  return `${platform} · ${browser}`.slice(0, 200);
}

function guessPlatform(ua: string): string {
  if (/Mac/.test(ua)) return "macOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  if (/iPhone|iPad/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  return "Unknown";
}

function guessBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  return "Browser";
}
